import type {
  DefaultContext,
  MaybeMasked,
  OperationVariables,
  WatchQueryFetchPolicy,
} from '@apollo/client';
import { Box, Loader, Notice, Text } from 'braid-design-system';
import type { DocumentNode } from 'graphql';
import type { ComponentProps } from 'react';
import type React from 'react';
import { ErrorNotice } from '../ErrorNotice/ErrorNotice.tsx';
import { useQuery } from '@apollo/client/react';

type QueryResultWithDefiniteData<
  Data,
  Variables extends OperationVariables,
> = Omit<useQuery.Result<Data, Variables>, 'data'> & {
  data: MaybeMasked<Data>;
};

export interface QueryProps<Data, Variables extends OperationVariables> {
  query: DocumentNode;
  errorMessage: string;
  variables: Variables;
  children: (
    result: QueryResultWithDefiniteData<Data, Variables>,
  ) => React.JSX.Element | null;
  loaderSize?: ComponentProps<typeof Loader>['size'];
  noCache?: boolean;
  context?: Partial<DefaultContext>;
  notifyOnNetworkStatusChange?: boolean;
}

export const Query = <
  Data,
  Variables extends OperationVariables = Record<string, never>,
>(
  props: QueryProps<Data, Variables>,
) => {
  const fetchPolicy: WatchQueryFetchPolicy = props.noCache
    ? 'no-cache'
    : 'cache-first';
  const { query, errorMessage, children, context, variables } = props;

  const queryResult = useQuery<Data, Variables>(query, {
    fetchPolicy,
    variables: variables!,
    context,
    notifyOnNetworkStatusChange: props.notifyOnNetworkStatusChange,
  });

  const { loading, error, data } = queryResult;

  if (loading) {
    return (
      <Box height="full" width="full" display="flex" justifyContent="center">
        <Loader size={props.loaderSize ?? 'large'} />
      </Box>
    );
  }

  if (error && !data) {
    return <ErrorNotice description={error.message} title={errorMessage} />;
  }

  if (!data) {
    return (
      <Notice tone="critical">
        <Text>No data was returned in the request.</Text>
      </Notice>
    );
  }

  return children({ ...queryResult, data });
};
